import { Fragment, useMemo } from "react";
import { cn } from "@nexus/ui";
import { parseMarkdown, type MarkdownAlign, type MarkdownBlock, type MarkdownInline } from "@/features/ask-nexus/lib/markdown";

/** Renders an agent answer. Text is only ever placed as React text, never as HTML. */
export function AskNexusMarkdown({ text }: { text: string }) {
  const blocks = useMemo(() => parseMarkdown(text), [text]);
  return (
    <div className="flex flex-col gap-4 text-sm leading-[1.6] text-pretty" data-testid="ask-nexus-answer">
      {blocks.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </div>
  );
}

function Block({ block }: { block: MarkdownBlock }) {
  switch (block.type) {
    case "heading":
      return (
        <p className={block.level === 1 ? "text-base font-medium" : "text-sm font-medium"}>
          <Inlines nodes={block.children} />
        </p>
      );
    case "paragraph":
      return (
        <p>
          <Inlines nodes={block.children} />
        </p>
      );
    case "list": {
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag className={block.ordered ? "list-decimal pl-5 marker:text-(--an-ink-3)" : "list-disc pl-5 marker:text-(--an-ink-3)"}>
          {block.items.map((item, index) => (
            <li key={index} className="mb-1.5 leading-[1.55]">
              <Inlines nodes={item} />
            </li>
          ))}
        </ListTag>
      );
    }
    case "table":
      return (
        <div className="overflow-x-auto rounded-2xl border border-(--an-hair) bg-(--an-field) p-4">
          <table className="w-full border-collapse text-xs tabular-nums">
            <thead>
              <tr className="border-b border-(--an-hair-strong)">
                {block.header.map((cell, index) => (
                  <th key={index} className={cn("pr-2 pb-2 font-medium whitespace-nowrap text-(--an-ink-2)", alignClass(block.align[index] ?? null))}>
                    <Inlines nodes={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-(--an-hair) last:border-b-0">
                  {row.map((cell, index) => (
                    <td key={index} className={cn("py-[9px] pr-2 align-baseline", alignClass(block.align[index] ?? null))}>
                      <Inlines nodes={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "code":
      return <pre className="overflow-x-auto rounded-xl bg-(--an-inset) p-3 font-mono text-xs">{block.text}</pre>;
  }
}

const ALIGN_CLASSES: Record<NonNullable<MarkdownAlign>, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

function alignClass(align: MarkdownAlign): string {
  return align ? ALIGN_CLASSES[align] : "text-left";
}

function Inlines({ nodes }: { nodes: MarkdownInline[] }) {
  return (
    <>
      {nodes.map((node, index) => (
        <Fragment key={index}>{renderInline(node)}</Fragment>
      ))}
    </>
  );
}

function renderInline(node: MarkdownInline) {
  switch (node.type) {
    case "text":
      return node.text;
    case "strong":
      return (
        <strong className="font-medium">
          <Inlines nodes={node.children} />
        </strong>
      );
    case "em":
      return (
        <em>
          <Inlines nodes={node.children} />
        </em>
      );
    case "code":
      return <code className="rounded bg-(--an-inset) px-1 py-0.5 font-mono text-xs">{node.text}</code>;
    case "link":
      return (
        <a href={node.href} target="_blank" rel="noopener noreferrer" className="underline decoration-(--an-hair-dashed) underline-offset-2 hover:decoration-current">
          <Inlines nodes={node.children} />
        </a>
      );
  }
}
